import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiKey, FiX, FiList, FiGrid, FiFile, FiFileText, FiEye, FiEyeOff } from 'react-icons/fi';
import PinModal from '../components/PinModal';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pinAction, setPinAction] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [showPassword, setShowPassword] = useState(false); // Pour afficher/masquer le mot de passe
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'caissier',
    shop_id: ''
  });
  const [passwordData, setPasswordData] = useState({ password: '', confirm_password: '' });

  const isAdmin = currentUser?.role === 'admin';
  if (!isAdmin) return null;

  useEffect(() => { fetchUsers(); fetchShops(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const fetchShops = async () => {
    try {
      const response = await api.get('/shops');
      setShops(response.data);
    } catch (error) {}
  };

  const executeAction = async () => {
    try {
      if (pinAction === 'delete') {
        await api.delete(`/users/${selectedUser.id}`, { data: { pin: '020260' } });
        toast.success('Utilisateur supprimé');
        fetchUsers();
      } else if (pinAction === 'update') {
        if (selectedUser) {
          await api.put(`/users/${selectedUser.id}`, { ...formData, pin: '020260' });
          toast.success('Utilisateur modifié');
        } else {
          await api.post('/users', { ...formData, pin: '020260' });
          toast.success('Utilisateur ajouté');
        }
        setShowModal(false);
        setFormData({ email: '', name: '', password: '', role: 'caissier', shop_id: '' });
        setSelectedUser(null);
        fetchUsers();
      } else if (pinAction === 'password') {
        await api.put(`/users/${selectedUser.id}/password`, { password: passwordData.password, pin: '020260' });
        toast.success('Mot de passe modifié');
        setShowPasswordModal(false);
        setPasswordData({ password: '', confirm_password: '' });
      }
    } catch (error) { toast.error(error.response?.data?.message || 'Erreur'); }
  };

  const handleSubmit = (e) => { e.preventDefault(); setPinAction('update'); setShowPinModal(true); };
  
  const handleChangePassword = (e) => { 
    e.preventDefault(); 
    if (passwordData.password !== passwordData.confirm_password) { 
      toast.error('Les mots de passe ne correspondent pas'); 
      return; 
    }
    if (passwordData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setPinAction('password'); 
    setShowPinModal(true); 
  };
  
  const handleDelete = (user) => { 
    if (user.id === currentUser?.id) { 
      toast.error('Vous ne pouvez pas vous supprimer'); 
      return; 
    } 
    setSelectedUser(user); 
    setPinAction('delete'); 
    setShowPinModal(true); 
  };
  
  const openEditModal = (user) => { 
    setSelectedUser(user); 
    setFormData({ 
      email: user.email, 
      name: user.name, 
      password: '', 
      role: user.role, 
      shop_id: user.shop_id || '' 
    }); 
    setShowModal(true); 
  };
  
  const openPasswordModal = (user) => { 
    setSelectedUser(user); 
    setPasswordData({ password: '', confirm_password: '' }); 
    setShowPasswordModal(true); 
  };

  const exportToExcel = () => {
    const exportData = users.map(u => ({ 'Nom': u.name, 'Email': u.email, 'Rôle': u.role === 'admin' ? 'Admin' : u.role === 'caissier' ? 'Caissier' : 'Technicien', 'Date': new Date(u.created_at).toLocaleDateString('fr-FR') }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Utilisateurs');
    XLSX.writeFile(wb, `utilisateurs_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Export Excel réussi');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFontSize(18);
    doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('Liste des utilisateurs', pageWidth / 2, 32, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.text(`Total: ${users.length}`, 14, 55);
    const tableHeaders = [['Nom', 'Email', 'Rôle']];
    const tableRows = users.slice(0, 50).map(u => [u.name, u.email, u.role === 'admin' ? 'Admin' : u.role === 'caissier' ? 'Caissier' : 'Technicien']);
    doc.autoTable({ startY: 65, head: tableHeaders, body: tableRows, theme: 'striped', styles: { fontSize: 8 }, headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] } });
    doc.save(`utilisateurs_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF réussi');
  };

  const getRoleBadge = (role) => ({ admin: 'bg-purple-100 text-purple-800', caissier: 'bg-blue-100 text-blue-800', technicien: 'bg-green-100 text-green-800' }[role] || 'bg-gray-100');
  const getRoleLabel = (role) => ({ admin: 'Administrateur', caissier: 'Caissier', technicien: 'Technicien' }[role] || role);

  if (loading) return <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mt-20"></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold">Utilisateurs</h1>
        <div className="flex gap-3">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFile size={18} /> Excel</button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFileText size={18} /> PDF</button>
          <div className="flex gap-2">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}><FiList /></button>
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}><FiGrid /></button>
          </div>
          <button onClick={() => { setSelectedUser(null); setFormData({ email: '', name: '', password: '', role: 'caissier', shop_id: '' }); setShowModal(true); }} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiPlus size={18} /> Nouvel utilisateur</button>
        </div>
      </div>

      {/* Vue Liste */}
      {viewMode === 'list' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3">Nom</th>
                <th className="pb-3">Email</th>
                <th className="pb-3">Rôle</th>
                <th className="pb-3">Date</th>
                <th className="pb-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 font-medium">{user.name} {user.id === currentUser?.id && <span className="text-xs text-green-600">(Vous)</span>}</td>
                  <td className="py-3">{user.email}</td>
                  <td className="py-3"><span className={`px-2 py-1 rounded-full text-xs ${getRoleBadge(user.role)}`}>{getRoleLabel(user.role)}</span></td>
                  <td className="py-3">{new Date(user.created_at).toLocaleDateString('fr-FR')}</td>
                  <td className="py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => openEditModal(user)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><FiEdit2 size={16} /></button>
                      <button onClick={() => openPasswordModal(user)} className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"><FiKey size={16} /></button>
                      {user.id !== currentUser?.id && <button onClick={() => handleDelete(user)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FiTrash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Vue Grille */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map(user => (
            <div key={user.id} className="card">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{user.name}</h3>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${getRoleBadge(user.role)}`}>{getRoleLabel(user.role)}</span>
              </div>
              <div className="border-t pt-3 mt-2">
                <p className="text-xs text-gray-400">Créé le: {new Date(user.created_at).toLocaleDateString('fr-FR')}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => openEditModal(user)} className="flex-1 bg-green-600 text-white py-1 rounded text-sm">Modifier</button>
                  {user.id !== currentUser?.id && <button onClick={() => handleDelete(user)} className="flex-1 bg-red-600 text-white py-1 rounded text-sm">Supprimer</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Ajout/Modification avec View Password */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{selectedUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><FiX size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Champ Nom */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              {/* Champ Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              {/* Champ Mot de passe avec View Password */}
              {!selectedUser && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input-field pr-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Minimum 6 caractères</p>
                </div>
              )}

              {/* Champ Rôle */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  className="input-field"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="caissier">Caissier</option>
                  <option value="technicien">Technicien</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>

              {/* Champ Boutique */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Boutique</label>
                <select
                  className="input-field"
                  value={formData.shop_id}
                  onChange={(e) => setFormData({ ...formData, shop_id: e.target.value })}
                >
                  <option value="">Sélectionner une boutique</option>
                  {shops.map(shop => <option key={shop.id} value={shop.id}>{shop.name}</option>)}
                </select>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 btn-secondary">Annuler</button>
                <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors">
                  {selectedUser ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Changement mot de passe avec View Password */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Changer le mot de passe - {selectedUser.name}</h2>
              <button onClick={() => setShowPasswordModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><FiX size={20} /></button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    value={passwordData.password}
                    onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 btn-secondary">Annuler</button>
                <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg">Changer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <PinModal isOpen={showPinModal} onClose={() => { setShowPinModal(false); setPinAction(null); }} onConfirm={executeAction} title="Code PIN requis" message="Entrez le code PIN" />
    </div>
  );
};

export default Users;